// src/components/TeachingPlanCard/JourneyOverview2D.js
import React, { useRef, useState, useLayoutEffect, useEffect, useMemo } from "react";
import { Box, Typography, Paper, IconButton, Tooltip } from "@mui/material";
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import FilterCenterFocusIcon from '@mui/icons-material/FilterCenterFocus';
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";

import Journey2DCard from "./Journey2DCard";
import Journey2DPathOverlay from "./Journey2DPathOverlay";
import { FRAMEWORK_COLORS, DIFFICULTY_LEVELS, parseMluCode } from "./Journey2DUtils";
import { getCatalog } from "../../api";

// Connecting arrows between cards were confusing (they cross quadrants and imply
// a strict linear order). Set to true to re-enable the SVG path overlay.
const SHOW_JOURNEY_ARROWS = false;

// The map is laid out at MAP_WIDTH and scaled to fit the frame, so a phone sees all of it at first.
const MAP_WIDTH = 1200;
const DEFAULT_SCALE = 0.75;

export default function JourneyOverview2D({ items }) {
    const containerRef = useRef(null);
    const cardRefs = useRef([]);
    const [coords, setCoords] = useState([]);
    const [fullCatalog, setFullCatalog] = useState([]);
    const frameRef = useRef(null);
    const [frameWidth, setFrameWidth] = useState(0);

    // Measure the frame once laid out (and on resize) to pick an initial zoom that fits it
    useLayoutEffect(() => {
        const el = frameRef.current;
        if (!el) return undefined;
        const measure = () => setFrameWidth(el.clientWidth);
        measure();
        window.addEventListener("resize", measure);
        return () => window.removeEventListener("resize", measure);
    }, []);
    const fitScale = frameWidth ? Math.min(DEFAULT_SCALE, (frameWidth - 8) / MAP_WIDTH) : DEFAULT_SCALE;
    const initialScale = Math.round(fitScale * 100) / 100;

    // Fetch master catalog dynamically from the backend for Tooltip population
    useEffect(() => {
        let active = true;
        const fetchCatalog = async () => {
            try {
                const res = await getCatalog();
                if (active && res.ok && Array.isArray(res.items)) {
                    setFullCatalog(res.items);
                }
            } catch (err) {
                console.error("Failed to fetch catalog for Journey Map", err);
            }
        };
        fetchCatalog();
        return () => { active = false; };
    }, []);

    // Map flat ordered items into the 2D layout structure: data[Framework][Difficulty]
    const matrixData = useMemo(() => {
        const data = {
            EDU: { beg: [], int: [], adv: [] },
            EID: { beg: [], int: [], adv: [] },
            TEC: { beg: [], int: [], adv: [] },
            GOV: { beg: [], int: [], adv: [] },
            DEFAULT: { beg: [], int: [], adv: [] },
        };

        if (!items) return data;

        items.forEach((item, originalIndex) => {
            const { prefix, difficultyId, config } = parseMluCode(item.code);
            const safePrefix = data[prefix] ? prefix : "DEFAULT";

            if (data[safePrefix][difficultyId]) {
                data[safePrefix][difficultyId].push({
                    mlu: item,
                    originalIndex,
                    config
                });
            }
        });

        return data;
    }, [items]);

    // Read card geometries to draw the SVG lines (immune to CSS scaling from Zoom layer)
    useLayoutEffect(() => {
        if (!SHOW_JOURNEY_ARROWS) return;
        if (!items || items.length === 0) return;

        const calculateCoords = () => {
            if (!containerRef.current) return;

            const newCoords = items.map((_, idx) => {
                const cardNode = cardRefs.current[idx];
                if (!cardNode) return null;

                let x = 0;
                let y = 0;
                let el = cardNode;

                // Traverse up offsetParents to compute local unscaled coordinates
                while (el && el !== containerRef.current) {
                    x += el.offsetLeft;
                    y += el.offsetTop;
                    el = el.offsetParent;
                }

                return {
                    x: x + (cardNode.offsetWidth / 2),
                    y: y + (cardNode.offsetHeight / 2),
                };
            }).filter(Boolean);

            setCoords(newCoords);
        };

        calculateCoords();
        const timeoutId = setTimeout(calculateCoords, 100);
        window.addEventListener("resize", calculateCoords);

        return () => {
            window.removeEventListener("resize", calculateCoords);
            clearTimeout(timeoutId);
        };
    }, [items]);

    if (!items || items.length === 0) return null;

    const renderQuadrant = (fwKey) => {
        const fwData = matrixData[fwKey];
        const config = FRAMEWORK_COLORS[fwKey] || FRAMEWORK_COLORS.DEFAULT;

        return (
            <Paper
                key={fwKey}
                variant="outlined"
                sx={{
                    border: `2px solid ${config.main}`,
                    borderRadius: 2,
                    bgcolor: "#ffffff",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden"
                }}
            >
                {/* Quadrant Header */}
                <Box sx={{ bgcolor: config.bg, py: 1, px: 2, borderBottom: `2px solid ${config.main}` }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: config.main, textAlign: "center" }}>
                        {config.label} ({fwKey})
                    </Typography>
                </Box>

                {/* Quadrant Body: 3 columns for Beg, Int, Adv */}
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", flexGrow: 1 }}>
                    {DIFFICULTY_LEVELS.map((level, idx) => (
                        <Box
                            key={level.id}
                            sx={{
                                p: 1.5,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 1.5,
                                borderRight: idx < 2 ? `1px dashed ${config.main}40` : "none"
                            }}
                        >
                            <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", opacity: 0.8, textTransform: "uppercase", letterSpacing: 0.5, mb: 0.5 }}>
                                {level.label}
                            </Typography>

                            {fwData[level.id].map((cellData) => (
                                <Journey2DCard
                                    key={`mlu-${cellData.originalIndex}`}
                                    mlu={cellData.mlu}
                                    stepIndex={cellData.originalIndex + 1}
                                    config={cellData.config}
                                    fullCatalog={fullCatalog}
                                    ref={(el) => (cardRefs.current[cellData.originalIndex] = el)}
                                />
                            ))}
                        </Box>
                    ))}
                </Box>
            </Paper>
        );
    };

    return (
        <Box sx={{ mt: 3, mb: 1 }}>
            <Typography variant="body1" sx={{ mb: 2, fontWeight: 700, color: "text.primary" }}>
                Journey Map ({items.length} Modules)
            </Typography>

            <Box
                ref={frameRef}
                sx={{
                    width: "100%",
                    height: initialScale < DEFAULT_SCALE ? Math.max(240, Math.round(700 * initialScale)) : 550,
                    bgcolor: "#fafafa",
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    position: "relative",
                    overflow: "hidden" // keep pinch scaling bounds tucked neatly inside this frame
                }}
            >
                {frameWidth > 0 && (
                <TransformWrapper
                    key={initialScale}
                    initialScale={initialScale}
                    minScale={Math.min(0.3, initialScale)}
                    maxScale={3}
                    centerOnInit={true}
                    wheel={{ step: 0.1 }}
                >
                    {({ zoomIn, zoomOut, resetTransform }) => (
                        <>
                            {/* Floating Toolbar inside the map */}
                            <Box sx={{ position: "absolute", top: 12, right: 12, zIndex: 1000, display: "flex", gap: 0.5, bgcolor: "background.paper", boxShadow: "0px 2px 4px rgba(0,0,0,0.1)", borderRadius: 2, p: 0.5 }}>
                                <Tooltip title="Zoom In" placement="top">
                                    <IconButton size="small" onClick={() => zoomIn()}>
                                        <ZoomInIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Zoom Out" placement="top">
                                    <IconButton size="small" onClick={() => zoomOut()}>
                                        <ZoomOutIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Reset View" placement="top">
                                    <IconButton size="small" onClick={() => resetTransform()}>
                                        <FilterCenterFocusIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>

                            <TransformComponent wrapperStyle={{ width: "100%", height: "100%" }}>
                                <Box
                                    id="journey-map-capture-node"
                                    ref={containerRef}
                                    sx={{
                                        position: "relative",
                                        minWidth: MAP_WIDTH, // ensure grid remains uncompressed, but allows the node to expand to its scrollWidth if children need more space
                                        p: 3,
                                        bgcolor: "#fafafa",
                                    }}
                                >
                                    {/* 1. Underlying CSS Grid Layer (2x2 Quadrants) */}
                                    <Box
                                        sx={{
                                            display: "grid",
                                            gridTemplateColumns: "1fr 1fr",
                                            gridTemplateRows: "1fr 1fr",
                                            gap: 3,
                                            position: "relative",
                                            zIndex: 2,
                                        }}
                                    >
                                        {renderQuadrant("EDU")}
                                        {renderQuadrant("EID")}
                                        {renderQuadrant("TEC")}
                                        {renderQuadrant("GOV")}
                                    </Box>

                                    {/* 2. Top-level Path Layer (z-index 10) - hidden during image export */}
                                    {SHOW_JOURNEY_ARROWS && (
                                        <Box data-html2canvas-ignore>
                                            <Journey2DPathOverlay coords={coords} />
                                        </Box>
                                    )}
                                </Box>
                            </TransformComponent>
                        </>
                    )}
                </TransformWrapper>
                )}
            </Box>
        </Box>
    );
}
